import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog.js';
import { Button, buttonVariants } from '@/components/ui/button.js';
import { Badge } from '@/components/ui/badge.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import {
  cancelCoachAssessmentReviewRequest,
  getCoachTqReviews,
  requestCoachAssessmentReview,
} from '@/lib/api-calls.js';
import { airServerErrorFrom } from '@/lib/error-handling.js';
import { CoachTqAssessmentRepeatAction, type CoachTqReview } from '@airreading/common/data';

const actionLinkClassName = 'h-auto p-0 text-blue-600 hover:text-blue-800 no-underline';

interface AssessmentRepeatReviewActionProps {
  coachId: string;
  review: CoachTqReview;
}

export function AssessmentRepeatReviewAction({ coachId, review }: AssessmentRepeatReviewActionProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const invalidateReviews = () => queryClient.invalidateQueries({ queryKey: getCoachTqReviews(coachId).queryKey });

  const requestMutation = useMutation({
    mutationFn: () => requestCoachAssessmentReview(coachId),
    onSuccess: async () => {
      await invalidateReviews();
      setConfirmOpen(false);
    },
    onError: (error: unknown) => {
      toast({
        description:
          airServerErrorFrom(error)?.userFacingMessage ??
          'Failed to request another Assessment review. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => cancelCoachAssessmentReviewRequest(coachId),
    onSuccess: async () => {
      await invalidateReviews();
    },
    onError: (error: unknown) => {
      toast({
        description:
          airServerErrorFrom(error)?.userFacingMessage ??
          'Failed to cancel the Assessment review request. Please try again.',
        variant: 'destructive',
      });
    },
  });

  if (review.assessmentRepeatAction === CoachTqAssessmentRepeatAction.waiting) {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Badge
          variant="secondary"
          className="rounded-full border-transparent bg-slate-100 font-medium text-slate-600 hover:bg-slate-100"
        >
          Another review required
        </Badge>
        <Button
          type="button"
          variant="link"
          size="sm"
          className={actionLinkClassName}
          disabled={cancelMutation.isPending}
          onClick={() => cancelMutation.mutate()}
        >
          Cancel request
        </Button>
      </div>
    );
  }

  if (review.assessmentRepeatAction !== CoachTqAssessmentRepeatAction.request) {
    return <span>—</span>;
  }

  return (
    <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
      <AlertDialogTrigger asChild>
        <Button type="button" variant="link" size="sm" className={actionLinkClassName}>
          Request another review
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>Request another assessment review?</AlertDialogTitle>
          <AlertDialogDescription>
            The teacher will be flagged for another Assessment review after completing a new diagnostic session.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={requestMutation.isPending} className={buttonVariants({ variant: 'dismiss' })}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={requestMutation.isPending}
            onClick={(event) => {
              event.preventDefault();
              requestMutation.mutate();
            }}
          >
            Request review
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
